/* eslint-disable jira/import-whitelist */
import type { PreloadedQuery } from 'react-relay';
import { JiraViewAri } from '@atlassian/ari/jira';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import type { UIFBoardCachedDataResult } from '@jira/software__uif-early-script/src/fetchUIFBoardData.tsx';
import parameters from '@jira/spa-apps__next-gen-board/src/__generated__/BoardViewSettingsDataResolverQuery$parameters.ts';
import type { BoardViewSettingsDataResolverQuery } from '@jira/spa-apps__next-gen-board/src/__generated__/BoardViewSettingsDataResolverQuery.graphql.ts';

const RESOURCE_TYPE_UIF_BOARD = 'UIF_BOARD';

export const uifBoardResource: RouteResource<UIFBoardCachedDataResult | null> =
	createResource<UIFBoardCachedDataResult | null>({
		type: RESOURCE_TYPE_UIF_BOARD,
		getKey: ({
			match: {
				params: { boardId },
			},
			route: { name: routeName },
			query: { config },
		}) => {
			if (config) {
				return `uif-board::${routeName}::config`;
			}
			if (boardId == null) {
				return `uif-board::${routeName}::not-found`;
			}
			return `uif-board::${routeName}::${boardId}`;
		},
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-uif-board-resource", jiraSpaEntry: "async-uif-board-resource" */ './getUifBoardResourceData.tsx'
			),
		maxAge: 0,
	});

const getQuery: Parameters<
	typeof createRelayResource<BoardViewSettingsDataResolverQuery>
>[0]['getQuery'] = ({ match }, { tenantContext: { activationId, cloudId } }) => {
	const viewId = JiraViewAri.create({
		activationId,
		siteId: cloudId,
		scopeId: `${Number(match.params.boardId)}/board`,
		scopeType: 'board',
	}).toString();

	return {
		options: {
			fetchPolicy: 'network-only' as const,
		},
		parameters,
		variables: {
			input: {
				viewId,
			},
		},
	};
};

export const boardViewSettingsResource: RouteResource<
	PreloadedQuery<BoardViewSettingsDataResolverQuery, Record<string, unknown>>
> = createRelayResource<BoardViewSettingsDataResolverQuery>({
	type: 'BOARD_CMP_VIEW_SETTINGS_RESOURCE',
	getQuery,
});
